import { createContext, useContext, useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "bp_lang";

export const translations = {
  en: {
    nav: { services: "Services", work: "Work", why: "Why Us", reviews: "Reviews", contact: "Contact", estimate: "Free Estimate", admin: "Admin", adminLogin: "Admin Login", callEstimate: "Call for a Free Estimate" },
    hero: {
      badge: "Isaiah 40:31 · Amarillo, Texas",
      title: "Banman's Painting",
      subtitle: "Amarillo's premier interior & exterior painting craftsmen. We transform ordinary walls into statements of luxury — precision, depth, and finish worthy of your home.",
      cta: "Request a Free Estimate",
      secondary: "View Our Work",
      scroll: "Scroll",
    },
    marquee: "Elevating Amarillo Homes",
    services: {
      eyebrow: "What We Do",
      mvEyebrow: "Mission & Vision",
      missionTitle: "Our Mission",
      mission: "Banman's Painting LLC's mission is to deliver exceptional paint finishes, transforming residential and commercial spaces with precision and professionalism to exceed our clients' expectations.",
      visionTitle: "Our Vision",
      vision: "Our vision is to be the leading painting company in our region, recognized for the quality of our work, our commitment to the environment, and the total satisfaction of every client.",
      titleA: "Two disciplines.",
      titleB: "One standard.",
      intro: "From full interior repaints to weather-proof exteriors, every project is excellence.",
      viewGallery: "View Gallery",
      gallery: "Gallery",
      projects: "projects",
      close: "Close",
      interior: {
        title: "Interior Painting",
        desc: "Walls, ceilings, trim and accent features finished with flawless, brush-perfect precision that transforms every room.",
        tags: ["Walls & Ceilings", "Trim & Doors", "Color Consulting"],
      },
      exterior: {
        title: "Exterior Painting",
        desc: "Durable, weather-tested finishes that protect and elevate your home's first impression across the Texas panhandle.",
        tags: ["Siding", "Stucco", "Doors & Fascia"],
      },
    },
    portfolio: {
      eyebrow: "Selected Work",
      titleA: "A gallery of",
      titleB: "finished rooms.",
      items: {
        accent: { title: "Black & Gold Geometric Wall", cat: "Accent Wall" },
        bedroom: { title: "Office", cat: "Interior" },
        living: { title: "Grand Living Room", cat: "Interior" },
        closet: { title: "Custom Closet & Vanity", cat: "Architecture" },
      },
    },
    manifesto: {
      eyebrow: "Why Banman's",
      titleA: "The standard behind",
      titleB: "every stroke.",
      chapters: [
        { title: "Precision", body: "Crisp lines, immaculate edges, zero shortcuts. Every wall is prepped, taped and finished as if it were our own home — because our name is on it." },
        { title: "Premium Materials", body: "We use only trusted, long-lasting paints and finishes engineered for the Texas panhandle climate, so your color stays true for years." },
        { title: "Faith & Integrity", body: "Guided by Isaiah 40:31, we work with honesty, respect and diligence. Clear pricing, free estimates, and a finish we proudly stand behind." },
      ],
    },
    testimonials: {
      eyebrow: "Kind Words",
      titleA: "Trusted across",
      titleB: "the panhandle.",
      rating: "5.0 · Local Reviews",
      leaveReview: "Leave a Review",
      form: {
        title: "Share Your Experience",
        subtitle: "Rate our work and add a photo of your project.",
        name: "Your Name",
        location: "City / Area (optional)",
        rating: "Your Rating",
        review: "Your Review",
        reviewPh: "Tell us about your experience with Banman's Painting...",
        photo: "Add a Photo (optional)",
        photoHint: "JPG or PNG · we'll optimize it for you",
        changePhoto: "Change Photo",
        submit: "Submit Review",
        submitting: "Submitting...",
        cancel: "Cancel",
        success: "Thank you! Your review was submitted and will appear once approved.",
        error: "Something went wrong. Please try again.",
        errRequired: "Please add your name, a rating, and a short review.",
      },
      reviews: [
        { quote: "From start to finish the team was professional, punctual and respectful of our home. The results speak for themselves.", name: "Ashley R.", role: "Amarillo Homeowner" },
        { quote: "Fair pricing, clean work, and they finished right on schedule. It's rare to find a crew you can fully trust these days.", name: "Michael T.", role: "Canyon, TX" },
        { quote: "Great communication the whole way through and the attention to detail was outstanding. We'll be calling them again.", name: "Daniela G.", role: "Amarillo Homeowner" },
      ],
    },
    contact: {
      eyebrow: "Free Estimate",
      titleA: "Let's paint",
      titleB: "something beautiful.",
      intro: "Tell us about your project and we'll get back to you with a free, no-obligation estimate. Prefer to talk? Give us a call.",
      serviceArea: "Serving Amarillo & the Texas Panhandle",
      name: "Name", phone: "Phone", email: "Email", service: "Service", details: "Project Details",
      namePh: "Your name", phonePh: "806-000-0000", emailPh: "you@email.com", detailsPh: "Tell us about your space, rooms, colors...",
      submit: "Request My Free Estimate", sending: "Sending...",
      serviceOptions: ["Interior Painting", "Exterior Painting", "Not Sure Yet"],
      errRequired: "Please add your name and phone number.",
      success: "Thank you! We'll reach out shortly for your free estimate.",
      errGeneric: "Something went wrong. Please call us directly.",
    },
    footer: { explore: "Explore", callUs: "Call Us", serviceArea: "Service Area", area: "Amarillo & the Texas Panhandle", tagline: "Free estimates · Interior & Exterior", rights: "All rights reserved.", pro: "Professional Interior & Exterior Painting" },
    langModal: { title: "Choose your language", subtitle: "Elige tu idioma", en: "English", es: "Español" },
    admin: {
      loginTitle: "Admin Access", loginSubtitle: "Admin access only",
      email: "Email", password: "Password", signIn: "Sign In", signingIn: "Signing in...",
      backToSite: "Back to website", welcome: "Welcome back.", loginFailed: "Login failed", loggedOut: "Logged out",
      dashboard: "Admin Dashboard", viewSite: "View Site", logout: "Logout",
      tabs: { estimates: "Estimates", reviews: "Reviews", gallery: "Gallery" },
      all: "All",
      est: {
        total: "Total",
        statusLabels: { New: "New", Contacted: "Contacted", Done: "Done" },
        searchPh: "Search by name, phone, service...",
        loading: "Loading estimates...", emptyYet: "No estimates yet.", emptyFiltered: "No estimates marked",
        service: "Service", message: "Message", notes: "Private Notes", notesPh: "Add a note...",
        saveNotes: "Save Notes", del: "Delete",
        markedAs: "Marked as", notesSaved: "Notes saved", deleteConfirm: "Delete estimate from",
        deleted: "Estimate deleted", errStatus: "Could not update status", errNotes: "Could not save notes",
        errDelete: "Could not delete", errLoad: "Could not load estimates",
      },
      rev: {
        total: "Total", pending: "Pending", published: "Published",
        loading: "Loading reviews...", empty: "No reviews",
        approve: "Approve", hide: "Hide", del: "Delete",
        publishedToast: "Review published", hiddenToast: "Review hidden", deleted: "Review deleted",
        deleteConfirm: "Delete review from", errUpdate: "Could not update review", errDelete: "Could not delete review", errLoad: "Could not load reviews",
      },
      gal: {
        interior: "Interior", exterior: "Exterior", addPhotos: "Add Photos", uploading: "Uploading...",
        infoA: "photo", infoB: "photos in the", infoC: "gallery. The first photo is used as the card cover.",
        cover: "Cover", loading: "Loading...", empty: "No photos yet. Add your first one above.",
        addedOne: "Photo added", addedMany: "photos added", uploadFailed: "Upload failed",
        removeConfirm: "Remove this photo from the gallery?", removed: "Photo removed",
        errLoad: "Could not load gallery", errRemove: "Could not remove photo",
      },
    },
  },
  es: {
    nav: { services: "Servicios", work: "Trabajos", why: "Por Qué Nosotros", reviews: "Reseñas", contact: "Contacto", estimate: "Presupuesto Gratis", admin: "Admin", adminLogin: "Acceso Admin", callEstimate: "Llame para un presupuesto gratis" },
    hero: {
      badge: "Isaías 40:31 · Amarillo, Texas",
      title: "Banman's Painting",
      subtitle: "Los mejores pintores de interiores y exteriores de Amarillo. Transformamos paredes comunes en muestras de lujo: precisión, profundidad y un acabado digno de su hogar.",
      cta: "Solicite un Presupuesto Gratis",
      secondary: "Ver Nuestro Trabajo",
      scroll: "Desliza",
    },
    marquee: "Embelleciendo Hogares de Amarillo",
    services: {
      eyebrow: "Lo Que Hacemos",
      mvEyebrow: "Misión y Visión",
      missionTitle: "Nuestra Misión",
      mission: "La misión de Banman's Painting LLC es brindar acabados de pintura excepcionales, transformando espacios residenciales y comerciales con precisión y profesionalismo para superar las expectativas de nuestros clientes.",
      visionTitle: "Nuestra Visión",
      vision: "La visión es ser la empresa de pintura líder en nuestra región, reconocida por la calidad de nuestro trabajo, nuestro compromiso con el medio ambiente y la satisfacción total de cada cliente.",
      titleA: "Dos disciplinas.",
      titleB: "Un solo estándar.",
      intro: "Desde repintados completos de interiores hasta exteriores resistentes al clima, cada proyecto es excelencia.",
      viewGallery: "Ver Galería",
      gallery: "Galería",
      projects: "proyectos",
      close: "Cerrar",
      interior: {
        title: "Pintura de Interiores",
        desc: "Paredes, techos, molduras y muros de acento con un acabado impecable y precisión de brocha perfecta que transforma cada habitación.",
        tags: ["Paredes y Techos", "Molduras y Puertas", "Asesoría de Color"],
      },
      exterior: {
        title: "Pintura de Exteriores",
        desc: "Acabados duraderos y resistentes al clima que protegen y realzan la primera impresión de su hogar en la región de Texas.",
        tags: ["Revestimiento", "Estuco", "Puertas y Fascias"],
      },
    },
    portfolio: {
      eyebrow: "Trabajos Selectos",
      titleA: "Una galería de",
      titleB: "habitaciones terminadas.",
      items: {
        accent: { title: "Muro Geométrico Negro y Oro", cat: "Muro de Acento" },
        bedroom: { title: "Oficina", cat: "Interior" },
        living: { title: "Gran Sala de Estar", cat: "Interior" },
        closet: { title: "Clóset y Tocador a Medida", cat: "Arquitectura" },
      },
    },
    manifesto: {
      eyebrow: "Por Qué Banman's",
      titleA: "El estándar detrás de",
      titleB: "cada trazo.",
      chapters: [
        { title: "Precisión", body: "Líneas nítidas, bordes impecables, cero atajos. Cada pared se prepara, se protege y se termina como si fuera nuestro propio hogar, porque lleva nuestro nombre." },
        { title: "Materiales Premium", body: "Usamos solo pinturas y acabados confiables y duraderos, diseñados para el clima de Texas, para que su color se mantenga fiel por años." },
        { title: "Fe e Integridad", body: "Guiados por Isaías 40:31, trabajamos con honestidad, respeto y diligencia. Precios claros, presupuestos gratis y un acabado que respaldamos con orgullo." },
      ],
    },
    testimonials: {
      eyebrow: "Buenas Palabras",
      titleA: "De confianza en",
      titleB: "toda la región.",
      rating: "5.0 · Reseñas Locales",
      leaveReview: "Deja una Reseña",
      form: {
        title: "Comparte Tu Experiencia",
        subtitle: "Califica nuestro trabajo y añade una foto de tu proyecto.",
        name: "Tu Nombre",
        location: "Ciudad / Área (opcional)",
        rating: "Tu Calificación",
        review: "Tu Reseña",
        reviewPh: "Cuéntanos sobre tu experiencia con Banman's Painting...",
        photo: "Añadir una Foto (opcional)",
        photoHint: "JPG o PNG · la optimizaremos por ti",
        changePhoto: "Cambiar Foto",
        submit: "Enviar Reseña",
        submitting: "Enviando...",
        cancel: "Cancelar",
        success: "¡Gracias! Tu reseña fue enviada y aparecerá una vez aprobada.",
        error: "Algo salió mal. Por favor intenta de nuevo.",
        errRequired: "Por favor añade tu nombre, una calificación y una reseña breve.",
      },
      reviews: [
        { quote: "De principio a fin el equipo fue profesional, puntual y respetuoso con nuestro hogar. Los resultados hablan por sí solos.", name: "Ashley R.", role: "Propietaria en Amarillo" },
        { quote: "Precios justos, trabajo limpio y terminaron justo a tiempo. Es raro encontrar un equipo en el que puedas confiar por completo.", name: "Michael T.", role: "Canyon, TX" },
        { quote: "Excelente comunicación durante todo el proceso y una atención al detalle sobresaliente. Los volveremos a llamar.", name: "Daniela G.", role: "Propietaria en Amarillo" },
      ],
    },
    contact: {
      eyebrow: "Presupuesto Gratis",
      titleA: "Pintemos",
      titleB: "algo hermoso.",
      intro: "Cuéntenos sobre su proyecto y le responderemos con un presupuesto gratis y sin compromiso. ¿Prefiere hablar? Llámenos.",
      serviceArea: "Atendemos Amarillo y la región de Texas",
      name: "Nombre", phone: "Teléfono", email: "Correo", service: "Servicio", details: "Detalles del Proyecto",
      namePh: "Su nombre", phonePh: "806-000-0000", emailPh: "usted@correo.com", detailsPh: "Cuéntenos sobre su espacio, habitaciones, colores...",
      submit: "Solicitar Mi Presupuesto Gratis", sending: "Enviando...",
      serviceOptions: ["Pintura de Interiores", "Pintura de Exteriores", "Aún No Estoy Seguro"],
      errRequired: "Por favor añada su nombre y número de teléfono.",
      success: "¡Gracias! Nos pondremos en contacto pronto para su presupuesto gratis.",
      errGeneric: "Algo salió mal. Por favor llámenos directamente.",
    },
    footer: { explore: "Explorar", callUs: "Llámenos", serviceArea: "Área de Servicio", area: "Amarillo y la región de Texas", tagline: "Presupuestos gratis · Interiores y Exteriores", rights: "Todos los derechos reservados.", pro: "Pintura Profesional de Interiores y Exteriores" },
    langModal: { title: "Choose your language", subtitle: "Elige tu idioma", en: "English", es: "Español" },
    admin: {
      loginTitle: "Acceso Admin", loginSubtitle: "Solo acceso de administrador",
      email: "Correo", password: "Contraseña", signIn: "Iniciar Sesión", signingIn: "Iniciando sesión...",
      backToSite: "Volver al sitio", welcome: "Bienvenido de nuevo.", loginFailed: "Error al iniciar sesión", loggedOut: "Sesión cerrada",
      dashboard: "Panel de Administración", viewSite: "Ver Sitio", logout: "Cerrar Sesión",
      tabs: { estimates: "Presupuestos", reviews: "Reseñas", gallery: "Galería" },
      all: "Todos",
      est: {
        total: "Total",
        statusLabels: { New: "Nuevo", Contacted: "Contactado", Done: "Terminado" },
        searchPh: "Buscar por nombre, teléfono, servicio...",
        loading: "Cargando presupuestos...", emptyYet: "Aún no hay solicitudes.", emptyFiltered: "No hay solicitudes marcadas como",
        service: "Servicio", message: "Mensaje", notes: "Notas Privadas", notesPh: "Añadir una nota...",
        saveNotes: "Guardar Notas", del: "Eliminar",
        markedAs: "Marcado como", notesSaved: "Notas guardadas", deleteConfirm: "¿Eliminar la solicitud de",
        deleted: "Solicitud eliminada", errStatus: "No se pudo actualizar el estado", errNotes: "No se pudieron guardar las notas",
        errDelete: "No se pudo eliminar", errLoad: "No se pudieron cargar las solicitudes",
      },
      rev: {
        total: "Total", pending: "Pendientes", published: "Publicadas",
        loading: "Cargando reseñas...", empty: "No hay reseñas",
        approve: "Aprobar", hide: "Ocultar", del: "Eliminar",
        publishedToast: "Reseña publicada", hiddenToast: "Reseña ocultada", deleted: "Reseña eliminada",
        deleteConfirm: "¿Eliminar la reseña de", errUpdate: "No se pudo actualizar la reseña", errDelete: "No se pudo eliminar la reseña", errLoad: "No se pudieron cargar las reseñas",
      },
      gal: {
        interior: "Interior", exterior: "Exterior", addPhotos: "Añadir Fotos", uploading: "Subiendo...",
        infoA: "foto", infoB: "fotos en la galería de", infoC: ". La primera foto se usa como portada.",
        cover: "Portada", loading: "Cargando...", empty: "Aún no hay fotos. Añade la primera arriba.",
        addedOne: "Foto añadida", addedMany: "fotos añadidas", uploadFailed: "Error al subir",
        removeConfirm: "¿Quitar esta foto de la galería?", removed: "Foto eliminada",
        errLoad: "No se pudo cargar la galería", errRemove: "No se pudo eliminar la foto",
      },
    },
  },
};

const LangContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem(STORAGE_KEY) || null);

  const setLang = useCallback((l) => {
    localStorage.setItem(STORAGE_KEY, l);
    setLangState(l);
    document.documentElement.lang = l;
  }, []);

  useEffect(() => {
    if (lang) document.documentElement.lang = lang;
  }, [lang]);

  const t = translations[lang || "en"];
  return <LangContext.Provider value={{ lang, setLang, t }}>{children}</LangContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
